import { useMemo, useState } from "react";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { FileDown, FileText } from "lucide-react";

const STATUS = ["geplant", "unterwegs", "abgeschlossen", "storniert"];
const STATUS_LABEL = { geplant: "Geplant", unterwegs: "Unterwegs", abgeschlossen: "Abgeschlossen", storniert: "Storniert" };

function CheckList({ title, options, selected, onToggle, testid, disabled }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{title}</div>
      <div className={`max-h-36 overflow-y-auto rounded-md border border-border p-2 space-y-1.5 ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
        {options.length === 0 && <div className="text-xs text-muted-foreground italic px-1">Keine Einträge</div>}
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={selected.has(o.value)} onCheckedChange={() => onToggle(o.value)} data-testid={`${testid}-${o.value}`} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function TripExportDialog({ open, onOpenChange, kind, drivers, vehicles }) {
  const [all, setAll] = useState(true);
  const [drv, setDrv] = useState(new Set());
  const [plates, setPlates] = useState(new Set());
  const [statuses, setStatuses] = useState(new Set());
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [addresses, setAddresses] = useState("");

  const driverOpts = useMemo(() => drivers.map((d) => ({ value: d.id, label: `${d.firstname} ${d.lastname}` })), [drivers]);
  const plateOpts = useMemo(() => [...new Set(vehicles.map((v) => v.license_plate).filter(Boolean))].sort().map((p) => ({ value: p, label: p })), [vehicles]);
  const statusOpts = STATUS.map((s) => ({ value: s, label: STATUS_LABEL[s] }));

  const toggle = (setter) => (val) => setter((prev) => { const n = new Set(prev); n.has(val) ? n.delete(val) : n.add(val); return n; });

  const doExport = () => {
    const p = new URLSearchParams();
    p.set("all", String(all));
    if (!all) {
      drv.forEach((v) => p.append("driver_ids", v));
      plates.forEach((v) => p.append("plates", v));
      statuses.forEach((v) => p.append("statuses", v));
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      addresses.split(",").map((a) => a.trim()).filter(Boolean).forEach((a) => p.append("addresses", a));
    }
    window.open(`${API}/exports/trips.${kind}?${p.toString()}`, "_blank");
    onOpenChange(false);
  };

  const Icon = kind === "pdf" ? FileText : FileDown;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="trip-export-dialog">
        <DialogHeader>
          <DialogTitle>Fahrten exportieren ({kind.toUpperCase()})</DialogTitle>
          <DialogDescription>Alles exportieren oder gezielt nach Kategorien filtern. Mehrfachauswahl je Kategorie (ODER), Kategorien werden kombiniert (UND).</DialogDescription>
        </DialogHeader>

        <label className="flex items-center justify-between gap-3 rounded-md border border-border p-3 bg-muted/40">
          <span className="text-sm font-medium">Alles exportieren</span>
          <Switch checked={all} onCheckedChange={setAll} data-testid="trip-export-all-switch" />
        </label>

        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 ${all ? "opacity-40 pointer-events-none" : ""}`}>
          <CheckList title="Fahrer" options={driverOpts} selected={drv} onToggle={toggle(setDrv)} testid="trip-export-drivers" disabled={all} />
          <CheckList title="Kennzeichen" options={plateOpts} selected={plates} onToggle={toggle(setPlates)} testid="trip-export-plates" disabled={all} />
          <CheckList title="Status" options={statusOpts} selected={statuses} onToggle={toggle(setStatuses)} testid="trip-export-statuses" disabled={all} />
          <div className="sm:col-span-2 grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Zeitraum von</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="trip-export-date-from" /></div>
            <div><Label className="text-xs">Zeitraum bis</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="trip-export-date-to" /></div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Adressen (Orte/Straßen, mit Komma getrennt)</Label>
            <Input value={addresses} onChange={(e) => setAddresses(e.target.value)} placeholder="z.B. Berlin, Hauptstraße" data-testid="trip-export-addresses" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={doExport} data-testid="trip-export-confirm"><Icon className="h-4 w-4 mr-1" /> Exportieren</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
